// ═══════════════════════════════════════════════════════════════════════════════
//  src/pages/GimnasioPage.jsx
//  EL GIMNASIO — Hub de decisión de entrenamiento
//
//  Ruta: /entrenar  ·  Primera pantalla tras el login.
//
//  ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
//  Antes, al entrar el alumno caía directo en el temario A1→C2. Eso comunica
//  "curso", no "gimnasio": un temario es una fila de asientos, no una sala de
//  máquinas. Aquí el alumno ELIGE qué entrenar hoy — que es exactamente la
//  promesa del reposicionamiento (`docs/correccionesOnboarding.md`, Directiva
//  Global): el idioma no se estudia, se entrena, y el ritmo lo pone él.
//
//  El temario sigue existiendo intacto en /home; ahora es UNA de las opciones,
//  no la única puerta.
//
//  ── AISLAMIENTO ─────────────────────────────────────────────────────────────
//  Página nueva y autónoma. Reutiliza el Header existente (como el resto de la
//  app autenticada) pero no modifica ningún componente: su CSS va prefijado
//  `.gy-` y vive en su propio archivo.
//
//  ⚠️  TODA tarjeta apunta a una ruta que EXISTE hoy en App.jsx. Verificado
//      antes de escribir: una tarjeta sin destino real es un callejón sin
//      salida, y el discurso anti-humo de NSL empieza por no anunciar de más.
// ═══════════════════════════════════════════════════════════════════════════════

import { Link } from 'react-router-dom';
import Header from '../components/Header';
import usePageTracker from '../hooks/usePageTracker.js';
import './GimnasioPage.css';

/**
 * Las nueve estaciones del gimnasio.
 *
 * Aquí NO se declara color. Cada estación tenía el suyo y la rejilla parecía
 * una caja de lápices: nueve títulos de nueve colores compitiendo entre sí.
 * Ahora manda la marca — tinta en claro, amarillo en oscuro — y eso lo resuelve
 * el CSS con una sola variable.
 *
 * `wide` hace que la tarjeta ocupe la fila completa. Sólo la lleva el
 * diagnóstico: con más de una, las ocho restantes dejaban una huérfana en la
 * rejilla de dos columnas.
 */
const STATIONS = [
  {
    // Va PRIMERA a proposito: sin radiografia, todo lo demas es entrenar a
    // ciegas. Es la unica estacion que cambia lo que el sistema sabe del alumno.
    to: '/evaluacion',
    icon: '🩺',
    title: 'Mide tu condición',
    text: 'Antes de entrenar, veamos de dónde partes. Tu nivel real en cada habilidad, y en qué se te rompe.',
    wide: true,
  },
  {
    // Va SEGUNDA: es la estación de menor fricción de todo el gimnasio. Un
    // toque y ya estás entrenando, sin elegir nivel ni cargar lección. Para el
    // alumno que tiene 90 segundos, esta es la única puerta realista.
    to: '/dojo',
    icon: '⚡',
    title: 'El Dojo',
    text: 'Reflejos, patrones, ritmo y presión. Cuatro formatos de menos de minuto y medio.',
  },
  {
    // La sala del alumno que ya pasó de B1 y no encontraba dónde seguir: la
    // brecha que destapó la auditoría de niveles.
    to: '/alto-rendimiento',
    icon: '🏔️',
    title: 'Alto Rendimiento',
    text: 'B2 en adelante. Registro, subtexto, debate sostenido, escritura y oído nativo.',
  },
  {
    // Va TERCERA, justo detras de las dos puertas de menor friccion. Es la
    // unica estacion a la que se entra con una pregunta en vez de con un nivel:
    // el alumno que se atasca a mitad de una leccion no quiere otra leccion,
    // quiere que le desatasquen ESO. Enterrarla al final del gimnasio seria
    // pedirle que sepa buscar ayuda antes de saber que la necesita.
    to: '/consultorio',
    icon: '💡',
    title: 'El Consultorio',
    text: 'Trae tu duda escrita con tus palabras. Tres píldoras cortas y te pongo a prueba.',
  },
  {
    to: '/home',
    icon: '🎯',
    title: 'El Curso',
    text: 'Entrena desde cero. Tu ruta completa de A1 a C2, paso a paso.',
  },
  {
    // Va antes de Situaciones Reales porque es la más concreta de las dos: aquí
    // no se practica "una conversación", se practica LA entrevista del viernes.
    to: '/entrevista',
    icon: '🎯',
    title: 'Interview Room',
    text: 'Una entrevista completa en inglés con las cinco fases reales. Sales con un reporte de dónde te quiebras.',
  },
  {
    to: '/tutor',
    icon: '🎭',
    title: 'Situaciones Reales',
    text: 'Entrevistas, negociaciones, urgencias. Practica el momento antes del momento.',
  },
  {
    to: '/multigames',
    icon: '🎮',
    title: 'Mini Games',
    text: 'Gramática y vocabulario que no se sienten como estudiar.',
  },
  {
    to: '/glosario',
    icon: '📖',
    title: 'Glosario',
    text: 'Tu vocabulario, creciendo contigo. Cada palabra que se te atraviesa.',
  },
  {
    to: '/dashboard',
    icon: '📊',
    title: 'Tu Progreso',
    text: 'Los números reales de tu entrenamiento. Sin medallitas de participación.',
  },
];

/** Nombre del alumno, con degradación ordenada si el objeto viene incompleto. */
function getFirstName() {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.alias || user?.full_name?.split(' ')[0] || null;
  } catch {
    return null;
  }
}

// ── EL PERSONAJE ───────────────────────────────────────────────────────────
// Rob: «dependiendo la cuenta que se cree va a ser un Spacy Coach, que va a
// ser nuestro personaje principal». En el gimnasio del alumno el personaje es
// el entrenador; en Soluciones Empresariales ya existe "Spacy Business".
//
// Vive en una constante y no escrito dentro del JSX para que el dia que haya
// que elegirlo por tipo de cuenta se cambie AQUI y no cazando la cadena por
// media aplicacion.
const PERSONAJE = 'Spacy Coach';

export default function GimnasioPage() {
  usePageTracker('gimnasio', 'Hub de Entrenamiento', null, 'idle');

  const firstName = getFirstName();
  const nombre = firstName;

  return (
    <>
      <Header />

      <main className="gy-root">

        {/* ── La bienvenida del entrenador ──────────────────────────────────
            Mismo tratamiento que "Conoce a Spacy Business" en Soluciones
            Empresariales: la marca va dentro de un bloque inclinado, no
            suelta sobre el fondo. Alli el bloque es cian porque esa es la
            paleta de Business; aqui es el amarillo de NSL, que es el color
            del gimnasio y el mismo de la chapa "Tu gimnasio" de abajo. El
            amarillo se usa como RELLENO con texto negro encima, nunca como
            color de letra. */}
        <section className="gy-coach">
          <h2 className="gy-coach-titulo">
            {nombre ? `Bienvenido ${nombre}, ` : 'Bienvenido, '}
            es momento de entrenar con{' '}
            <span className="gy-coach-marca">{PERSONAJE}</span>
          </h2>
          <p className="gy-coach-sub">Es momento de ponerte a entrenar.</p>

          {/* La puerta al coach. Va aqui, pegada a su nombre, y no perdida
              entre las estaciones: no es una maquina mas del gimnasio, es
              quien decide que maquina te toca. */}
          <Link to="/coach" className="gy-coach-cta">
            ⚡ Comenzar entrenamiento
          </Link>
        </section>

        <section className="gy-panel">
          {/* Resplandor de fondo — profundidad sin costo de layout */}
          <div className="gy-panel-glow" aria-hidden="true" />

          <header className="gy-head">
            <p className="gy-kicker">Tu gimnasio</p>
            <h1 className="gy-title">
              {firstName ? `¿Qué vas a entrenar hoy, ${firstName}?` : '¿Qué vas a entrenar hoy?'}
            </h1>
            <p className="gy-sub">
              Sin rutina obligatoria. Eliges la máquina, eliges el peso.
            </p>
          </header>

          <div className="gy-grid">
            {STATIONS.map((s, i) => (
              <Link
                key={s.to}
                to={s.to}
                className={`gy-card${s.wide ? ' gy-card-wide' : ''}`}
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <span className="gy-card-icon" aria-hidden="true">{s.icon}</span>
                <h2 className="gy-card-title">{s.title}</h2>
                <p className="gy-card-text">{s.text}</p>
                <span className="gy-card-arrow" aria-hidden="true">→</span>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}
